<template>
  <div v-resize="onResize" ref="container" class="caption">
    <!-- Dumbbell chart -->
    <transition-group name="flip-dumbbell">
      <div v-for="d in sortedData" :key="d.key">
        <div class="bar-label">
          {{ d.label }}
        </div>
        <svg :width="chartWidth" :height="chartHeight">
          <g :transform="`translate(${margin.left},${margin.top})`">
            <g :transform="`translate(0,${height / 2})`">
              <line
                :x1="x(d.value[secondaryKey])"
                :x2="x(d.value[primaryKey])"
                stroke="#c4c4c4"
              ></line>
              <circle
                :cx="x(d.value[secondaryKey])"
                :r="dotRadius"
                :fill="colorScale(secondaryKey)"
              ></circle>
              <circle
                :cx="x(d.value[primaryKey])"
                :r="dotRadius"
                :fill="colorScale(primaryKey)"
              ></circle>
              <text
                :x="x(d.value.min) + (dotRadius + 4) * ($vuetify.rtl ? 1 : -1)"
                dy="0.32em"
                text-anchor="end"
              >
                {{ d.formated.min }}
              </text>
              <text
                :x="x(d.value.max) + (dotRadius + 4) * ($vuetify.rtl ? -1 : 1)"
                dy="0.32em"
              >
                {{ d.formated.max }}
              </text>
            </g>
          </g>
        </svg>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { scaleLinear, scaleOrdinal } from "d3-scale";
import { format } from "d3-format";
import { max } from "d3-array";
import AppChartLegend from "@/components/AppChartLegend.vue";
export default {
  props: { primaryKey: { type: String, required: true } },
  components: {
    AppChartLegend
  },
  data() {
    return {
      sortedOptions: ["prevalence", "severity"],
      chartWidth: 600,
      chartHeight: 28,
      margin: { top: 0, right: 56, bottom: 12, left: 20 },
      dotRadius: 8
    };
  },
  computed: {
    ...mapGetters([
      "filteredCount",
      "filteredBarChartExperienced",
      "filteredBarChartSevere"
    ]),
    secondaryKey() {
      return this.sortedOptions.find(key => key !== this.primaryKey);
    },
    rawData() {
      const experienced = this.filteredBarChartExperienced();
      const severe = this.filteredBarChartSevere();
      const rawData = [];
      for (let i = 0; i < experienced.length; i++) {
        const e = experienced[i];
        const s = severe[i];
        rawData.push({
          key: e.key,
          prevalence: e.value,
          severity: s.value
        });
      }
      return rawData;
    },
    percentageData() {
      return this.rawData.map(d => {
        const prevalence = d.prevalence ? d.prevalence / this.filteredCount : 0;
        const severity = d.severity ? d.severity / this.filteredCount : 0;
        const minValue = Math.min(prevalence, severity);
        const maxValue = Math.max(prevalence, severity);
        return {
          key: d.key,
          label: this.$t(`filters.legalProblems.legalProblem.options.${d.key}`),
          value: {
            prevalence,
            severity,
            min: minValue,
            max: maxValue
          },
          formated: {
            min: minValue ? format(".0%")(minValue) : "",
            max: maxValue ? format(".0%")(maxValue) : ""
          }
        };
      });
    },
    sortedData() {
      return this.percentageData.slice().sort(this.sortData);
    },
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    width() {
      return this.chartWidth - this.margin.left - this.margin.right;
    },
    height() {
      return this.chartHeight - this.margin.top - this.margin.bottom;
    },
    maxValue() {
      return max(this.percentageData, d =>
        Math.max(d.value.prevalence, d.value.severity)
      );
    },
    x() {
      return scaleLinear()
        .domain([0, this.maxValue])
        .range(this.$vuetify.rtl ? [this.width, 0] : [0, this.width])
        .nice();
    },
    colorScale() {
      return scaleOrdinal()
        .domain(["prevalence", "severity"])
        .range(["#194173", "#EB2227"]);
    },
    legendItems() {
      return this.sortedOptions.map(key => ({
        label: this.$t(`legalProblemLegend.${key}`),
        color: this.colorScale(key),
        shape: "circle"
      }));
    }
  },
  watch: {
    breakpoint() {
      this.onResize();
    }
  },
  methods: {
    sortData(a, b) {
      if (a.value[this.primaryKey] !== b.value[this.primaryKey]) {
        return b.value[this.primaryKey] - a.value[this.primaryKey];
      } else {
        return b.value[this.secondaryKey] - a.value[this.secondaryKey];
      }
    },
    onResize() {
      this.chartWidth = this.$refs.container.clientWidth;
    }
  }
};
</script>

<style scoped>
.flip-dumbbell-move {
  transition: transform 0.75s;
}
</style>
